import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { type EnqueueSnackbar, useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

import { customEventAtom } from '@/pages/Administration/Devices';
import { CustomEventTypes } from '@/pages/Administration/Devices/Devices.store';
import { axiosClient } from '@/services/BaseApi.service';

import {
  type DeviceSyncDataModel,
  groupeddevicesSyncDataAtom,
  groupedSyncItemsAtom,
  type SyncDevicesDialogMode,
  statusCodeDialogModeAtom,
  statusCodeDialogOpenAtom
} from './SyncDevices.types';

const showSnackbar = (mode: string, enqueueSnackbar: EnqueueSnackbar) => {
  let action = '';
  if (mode === 'confirmSyncDevices') {
    action = 'Devices Sync Initiated Successfully';
  }
  enqueueSnackbar(`${action}`, { variant: 'success' });
};

export const useSyncDevicesDialog = () => {
  const [error, setError] = useState('');
  const [mode, setMode] = useAtom(statusCodeDialogModeAtom);
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useAtom(statusCodeDialogOpenAtom);
  const [, setCustomEvent] = useAtom(customEventAtom);

  const openSyncDevicesDialog = (
    dialogMode: SyncDevicesDialogMode = 'newSyncDevices'
  ) => {
    setMode(dialogMode);
    setOpen(true);
  };

  const closeSyncDevicesDialog = () => {
    setOpen(false);
  };

  const mutation = useMutation({
    mutationFn: (payload: string[]) => {
      return axiosClient.post(`/devices_sync/sync`, { device_list: payload });
    },
    onSuccess: (_data) => {
      if (_data.status === 200 || _data.status === 201) {
        closeSyncDevicesDialog();
        showSnackbar(mode, enqueueSnackbar);
        setCustomEvent(CustomEventTypes.REFRESH_DEVICE_LISTING);
      } else {
        setError(
          `Something is wrong,\n ${_data?.data?.message}\n Please try again.`
        );
      }
    },
    onError: (err: Error | AxiosError) => {
      if (axios.isAxiosError(err)) {
        setError(`${err?.response?.data?.detail || 'Error saving device'}`);
      } else {
        setError(`Error saving device (${err.message})`);
      }
    }
  });

  return {
    open,
    error,
    mutation,
    mode,
    setError,
    openSyncDevicesDialog,
    closeSyncDevicesDialog
  };
};

const groupByOrganization = (data: DeviceSyncDataModel[]) => {
  const groupedData = data.reduce<Record<string, DeviceSyncDataModel[]>>(
    (acc, item) => {
      const organizationName =
        item.devices?.organization?.name ||
        item.devices_deployed?.organization?.name ||
        'Unknown';

      if (!acc[organizationName]) {
        acc[organizationName] = [];
      }

      acc[organizationName].push(item);

      return acc;
    },
    {}
  );

  return Object.entries(groupedData).map(([organization, items]) => ({
    organization,
    selectedCount: 0,
    items
  }));
};

// Fetch  Devices Sync by organization
export const useFetchDevicesSync = (orgLabel: string) => {
  const [, setGroupedDeviceSyncData] = useAtom(groupeddevicesSyncDataAtom);
  const [, setGroupedSyncData] = useAtom(groupedSyncItemsAtom);

  const deviceSyncQuery = useQuery({
    queryKey: [`organizations/${orgLabel}/devices_sync`]
  });

  useEffect(() => {
    if (deviceSyncQuery.isSuccess) {
      let respData: DeviceSyncDataModel[] =
        deviceSyncQuery.data as DeviceSyncDataModel[];
      respData = respData.map((deviceSyncRow) => {
        const devSyncRow = deviceSyncRow;
        let actionType = 'Updated';
        if (devSyncRow?.devices && devSyncRow?.devices_deployed) {
          actionType = 'Updated';
        }
        if (devSyncRow?.devices && !devSyncRow?.devices_deployed) {
          actionType = 'New';
        }
        if (!devSyncRow?.devices && devSyncRow?.devices_deployed) {
          actionType = 'Deleted';
        }
        const organizationName =
          devSyncRow.devices?.organization?.name ||
          devSyncRow.devices_deployed?.organization?.name ||
          '';
        const hostName =
          devSyncRow.devices?.host_name ||
          devSyncRow.devices_deployed?.host_name ||
          '';

        return {
          ...deviceSyncRow,
          actionType,
          orgName: organizationName,
          hostName
        };
      });

      const groupedSyncData = groupByOrganization(respData);
      setGroupedSyncData(groupedSyncData);
    }
    if (deviceSyncQuery.isError) {
      setGroupedDeviceSyncData([]);
      setGroupedSyncData([]);
    }
  }, [
    deviceSyncQuery.data,
    deviceSyncQuery.isSuccess,
    deviceSyncQuery.isError
  ]);

  return deviceSyncQuery;
};

export const useDevicesSyncStatus = () => {
  const [, setCustomEvent] = useAtom(customEventAtom);
  return useMutation({
    mutationFn: () => {
      return axiosClient.put(`/devices_sync/sync/status`);
    },
    onError: () => {
      // intentionally empty
    },
    onSuccess: () => {
      setCustomEvent(CustomEventTypes.REFRESH_DEVICE_LISTING);
    }
  });
};
